import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [sessionUser, setSessionUser] = useState(null);
  const apiRef = useRef(api);
  const formatErrorRef = useRef(formatApiErrorDetail);
  const { data: profile, isLoading, isError } = useQuery({ queryKey: ["auth-me"], queryFn: api.me, retry: false });
  const user = sessionUser || profile || (isError ? false : null);
  const checkingAuth = isLoading && user === null;

  const login = useCallback(async (payload) => {
    try {
      const data = await apiRef.current.login(payload);
      setSessionUser(data.user);
      return { ok: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return { ok: false, error: formatErrorRef.current(detail) };
    }
  }, [setSessionUser]);

  const register = useCallback(async (payload) => {
    try {
      const data = await apiRef.current.register(payload);
      setSessionUser(data.user);
      return { ok: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return { ok: false, error: formatErrorRef.current(detail) };
    }
  }, [setSessionUser]);

  const logout = useCallback(async () => {
    await apiRef.current.logout().catch(() => null);
    setSessionUser(false);
  }, [setSessionUser]);

  const updateUser = useCallback((updatedProfile) => {
    setSessionUser(updatedProfile);
  }, [setSessionUser]);

  const value = useMemo(() => {
    return { user, checkingAuth, login, register, logout, updateUser };
  }, [checkingAuth, login, logout, register, updateUser, user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}